import { ref, computed } from 'vue';
import type { FeatureError } from '@dh-io-owpi/backend-api/src/data-contracts';
import {
  getCategorizedFeatures,
  getCategorizedHighlightedFeatures,
  type Feature,
  type FeatureCategories,
  type FeatureCategory,
} from '../lib/api/api';
import type { AemData } from '../lib/types/aem';
import type { ExtendedFeatureCategories, ExtendedFeatureCategory } from '../lib/types/features';

/* istanbul ignore file for now */
// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export const useVehicleFeatures = (aemData: AemData, showHighlights?: boolean) => {
  const vehicleFeaturesData = ref<ExtendedFeatureCategories>();
  const errorMessage = ref('');
  const equipmentIdErrors = ref<FeatureError[]>([]);

  const equipmentIdErrorLabels = computed(() =>
    equipmentIdErrors.value.map(({ key, aemAuthorMessage }) => `${key}: ${aemAuthorMessage}`),
  );

  function mapFeatureElements(vehicleFeatures: FeatureCategories): ExtendedFeatureCategories {
    return {
      categories: vehicleFeatures.categories.map<ExtendedFeatureCategory>((featureCategory: FeatureCategory) => ({
        ...featureCategory,
        features: featureCategory.features.map((feature: Feature) => {
          const aemFeatureElement = aemData?.featureElements.find(
            (afe: { code: string }) => afe.code === feature.equipmentId,
          );

          return {
            ...feature,
            cta:
              aemFeatureElement?.ctaUrl && aemFeatureElement.ctaButtonText
                ? {
                    url: aemFeatureElement.ctaUrl,
                    label: aemFeatureElement.ctaButtonText,
                  }
                : undefined,
            hint: aemFeatureElement?.hint,
          };
        }),
      })),
    };
  }

  const { featureElements, country, language, modelSeries, subBrand } = aemData;

  function _getEndpoint() {
    const query = {
      country,
      language,
      modelSeries,
      'sub-brands': [subBrand],
    };

    if (showHighlights) return getCategorizedHighlightedFeatures(query, { retries: 3 });
    return getCategorizedFeatures(
      {
        ...query,
        equipmentIds: featureElements.map((featureElement) => featureElement.code),
      },
      { retries: 3 },
    );
  }

  if (showHighlights || (featureElements && featureElements.length > 0))
    _getEndpoint()
      .then(({ data }) => {
        equipmentIdErrors.value = data.errors;
        data.categories = data.categories.filter((category) => category.features.length);
        vehicleFeaturesData.value = showHighlights ? data : mapFeatureElements(data);
      })
      .catch(
        () => (errorMessage.value = 'Vehicle features: Unable to connect to the database.<br/>Please try again later.'),
      );

  return {
    vehicleFeaturesData,
    errorMessage,
    equipmentIdErrorLabels,
  };
};
